import { useState, useCallback, createContext, useContext, useEffect } from 'react';

interface SidebarContextType {
  collapsed: boolean;
  isLockedOpen: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggleSidebar: () => void;
  lockSidebarOpen: () => void;
  unlockSidebar: () => void;
}

export const SidebarContext = createContext<SidebarContextType>({
  collapsed: false,
  isLockedOpen: false,
  setCollapsed: () => {},
  toggleSidebar: () => {},
  lockSidebarOpen: () => {},
  unlockSidebar: () => {},
});

export function useSidebarProvider() {
  // Initialize state - always start collapsed for hover behavior
  const [collapsed, setCollapsedState] = useState(true); // Always start closed
  const [isLockedOpen, setIsLockedOpen] = useState(false);

  // When collapsed state changes, save to localStorage
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', collapsed.toString());
  }, [collapsed]);

  const toggleSidebar = useCallback(() => {
    setCollapsedState((prev) => {
      if (isLockedOpen) {
        return false;
      }
      return !prev;
    });
  }, [isLockedOpen]);

  // Add setCollapsed function for direct control
  const handleSetCollapsed = useCallback(
    (newCollapsed: boolean) => {
      setCollapsedState(() => {
        if (isLockedOpen && newCollapsed) {
          return false;
        }
        return newCollapsed;
      });
    },
    [isLockedOpen],
  );

  const lockSidebarOpen = useCallback(() => {
    setIsLockedOpen(true);
    setCollapsedState(false);
  }, []);

  const unlockSidebar = useCallback(() => {
    setIsLockedOpen(false);
  }, []);

  useEffect(() => {
    if (isLockedOpen) {
      setCollapsedState((prev) => (prev ? false : prev));
    }
  }, [isLockedOpen]);

  return {
    collapsed,
    isLockedOpen,
    setCollapsed: handleSetCollapsed,
    toggleSidebar,
    lockSidebarOpen,
    unlockSidebar,
  };
}

export function useSidebar() {
  const context = useContext(SidebarContext);

  if (context === undefined) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }

  return context;
}
